import React, { useState, useEffect, useRef } from 'react';
import { propertiesAPI, balanceAPI, Property, getImageUrl } from '../api/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const DashboardPage: React.FC = () => {
  const { user, hasPhone } = useAuth();
  const { t, language } = useLanguage();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<number>(0);
  const [listingPrice, setListingPrice] = useState<number>(10000);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [showPhoneRequiredModal, setShowPhoneRequiredModal] = useState(false);
  const [topupAmount, setTopupAmount] = useState('50000');
  const [topupInfo, setTopupInfo] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: 'apartment' as 'apartment' | 'house',
    price: '',
    area: '',
    rooms: '',
    bathrooms: '',
    floor: '',
    total_floors: '',
    address: '',
    city: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [propsRes, balRes, priceRes] = await Promise.all([
        propertiesAPI.getMyProperties(),
        balanceAPI.getBalance(),
        propertiesAPI.getListingPrice(),
      ]);
      setProperties(propsRes.data);
      setBalance(balRes.data.balance || 0);
      setListingPrice(priceRes.data.listing_price ?? 10000);
    } catch (err) {
      console.error('Failed to load dashboard data', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 7) {
      alert(t('uploadPhotosMaxError'));
      return;
    }
    const newPhotos = [...photos, ...files];
    setPhotos(newPhotos);
    const previews = newPhotos.map(f => URL.createObjectURL(f));
    setPhotoPreviews(previews);
  };

  const removePhoto = (idx: number) => {
    const newPhotos = photos.filter((_, i) => i !== idx);
    setPhotos(newPhotos);
    const previews = newPhotos.map(f => URL.createObjectURL(f));
    setPhotoPreviews(previews);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (photos.length < 2) {
      alert(t('uploadPhotosMinError'));
      return;
    }

    if (listingPrice > 0 && !user?.is_admin && balance < listingPrice) {
      alert(
        language === 'ru'
          ? `Недостаточно средств!\nТребуется: ${listingPrice.toLocaleString()} сум\nВаш баланс: ${balance.toLocaleString()} сум`
          : `Balansingiz yetarli emas!\nKerak: ${listingPrice.toLocaleString()} so'm\nBalansingiz: ${balance.toLocaleString()} so'm`
      );
      setShowTopupModal(true);
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        ...formData,
        price: Number(formData.price),
        area: Number(formData.area),
        rooms: formData.rooms ? Number(formData.rooms) : undefined,
        bathrooms: formData.bathrooms ? Number(formData.bathrooms) : undefined,
        floor: formData.floor ? Number(formData.floor) : undefined,
        total_floors: formData.total_floors ? Number(formData.total_floors) : undefined,
      };

      const res = await propertiesAPI.create(data);
      const propertyId = res.data.id;

      // Загружаем фото
      for (let i = 0; i < photos.length; i++) {
        await propertiesAPI.uploadImage(propertyId, photos[i], i === 0);
      }

      setShowCreateModal(false);
      resetForm();
      await loadData();
      alert(language === 'ru' ? 'Объявление отправлено на модерацию!' : "E'lon tekshiruvga yuborildi!");
    } catch (err: any) {
      const msg = err?.response?.data?.detail || (language === 'ru' ? 'Ошибка при создании' : 'Yaratishda xatolik');
      alert(`Xatolik: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', description: '', property_type: 'apartment',
      price: '', area: '', rooms: '', bathrooms: '',
      floor: '', total_floors: '', address: '', city: '',
    });
    setPhotos([]);
    setPhotoPreviews([]);
  };

  const handleTopupRequest = async () => {
    try {
      const res = await balanceAPI.requestTopup(Number(topupAmount));
      setTopupInfo(res.data);
    } catch (err) {
      console.error('Topup request failed', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        await propertiesAPI.delete(id);
        loadData();
      } catch (err) {
        console.error('Delete failed', err);
      }
    }
  };

  const handleEditClick = (property: Property) => {
    setEditingProperty(property);
    setFormData({
      title: property.title,
      description: property.description,
      property_type: property.property_type,
      price: String(property.price),
      area: String(property.area),
      rooms: property.rooms ? String(property.rooms) : '',
      bathrooms: property.bathrooms ? String(property.bathrooms) : '',
      floor: property.floor ? String(property.floor) : '',
      total_floors: property.total_floors ? String(property.total_floors) : '',
      address: property.address,
      city: property.city,
    });
    setPhotos([]);
    setPhotoPreviews(property.images.map(img => getImageUrl(img.image_url)));
    setShowEditModal(true);
  };

  const handleUpdateProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProperty) return;

    setSubmitting(true);
    try {
      const data = {
        ...formData,
        price: Number(formData.price),
        area: Number(formData.area),
        rooms: formData.rooms ? Number(formData.rooms) : undefined,
        bathrooms: formData.bathrooms ? Number(formData.bathrooms) : undefined,
        floor: formData.floor ? Number(formData.floor) : undefined,
        total_floors: formData.total_floors ? Number(formData.total_floors) : undefined,
      };

      await propertiesAPI.update(editingProperty.id, data);

      if (photos.length > 0) {
        const formDataImages = new FormData();
        photos.forEach(photo => formDataImages.append('files', photo));
        await propertiesAPI.uploadImages(editingProperty.id, formDataImages);
      }

      alert(language === 'ru' ? 'Объявление обновлено!' : "E'lon yangilandi!");
      setShowEditModal(false);
      setEditingProperty(null);
      setFormData({
        title: '',
        description: '',
        property_type: 'apartment',
        price: '',
        area: '',
        rooms: '',
        bathrooms: '',
        floor: '',
        total_floors: '',
        address: '',
        city: '',
      });
      setPhotos([]);
      setPhotoPreviews([]);
      loadData();
    } catch (err: any) {
      console.error('Update failed', err);
      alert(err.response?.data?.detail || (language === 'ru' ? 'Ошибка при обновлении' : 'Yangilashda xato'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateClick = () => {
    // Check if user has shared phone contact
    if (!hasPhone && !user?.is_admin) {
      setShowPhoneRequiredModal(true);
      return;
    }
    setShowCreateModal(true);
  };

  const openTelegramBot = () => {
    const botUsername = 'samuyjoy_bot'; // Replace with your actual bot username
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.openLink(`https://t.me/${botUsername}`);
    } else {
      window.open(`https://t.me/${botUsername}`, '_blank');
    }
  };

  const getStatusBadge = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'pending') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">
          ⏳ {t('pending')}
        </span>
      );
    }
    if (s === 'active') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800">
          ✅ {t('active')}
        </span>
      );
    }
    return (
      <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800">
        ❌ {t('rejected')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-24 md:pb-12">

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">{t('dashboard')}</h1>
          <p className="text-gray-500 text-xs sm:text-sm mt-0.5">{t('welcome')}, {user?.first_name || user?.username}!</p>
        </div>
        <button
          onClick={handleCreateClick}
          className="btn btn-primary text-xs sm:text-sm font-bold py-2.5 px-4 rounded-xl shadow active:scale-95"
        >
          + {t('createListing')}
        </button>
      </div>

      {/* Balance Widget */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-4 sm:p-6 text-white mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-lg">
        <div>
          <p className="text-primary-100 text-xs sm:text-sm font-medium">{t('yourBalance')}</p>
          <p className="text-2xl sm:text-3xl font-black mt-1">
            {balance.toLocaleString()} <span className="text-base sm:text-xl font-bold">{t('sum')}</span>
          </p>
          <p className="text-primary-200 text-xs mt-1">
            {t('costPerListing')}: {listingPrice === 0 ? t('freePosting') : `${listingPrice.toLocaleString()} ${t('sum')}`}
          </p>
        </div>
        <button
          onClick={() => setShowTopupModal(true)}
          className="bg-white text-primary-700 font-bold px-5 py-2.5 rounded-xl text-xs sm:text-sm hover:bg-primary-50 active:scale-95 transition shadow-sm self-start sm:self-auto"
        >
          + {t('topup')}
        </button>
      </div>

      {/* Property Listings */}
      {properties.length === 0 ? (
        <div className="text-center py-16 card p-6">
          <div className="text-5xl sm:text-6xl mb-3">🏠</div>
          <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-1">{t('noListings')}</h3>
          <p className="text-gray-500 text-xs sm:text-sm mb-6 max-w-sm mx-auto">{t('createFirstListing')}</p>
          <button onClick={handleCreateClick} className="btn btn-primary font-bold px-6 py-2.5">
            {t('createListing')}
          </button>
        </div>
      ) : (
        <div className="space-y-3.5">
          {properties.map((p) => {
            const firstImg = p.images?.[0]?.image_url ? getImageUrl(p.images[0].image_url) : null;
            return (
              <div key={p.id} className="card p-3.5 sm:p-4 flex gap-3.5 sm:gap-4 hover:shadow-md transition">
                {/* Photo */}
                <div className="w-24 sm:w-28 h-20 sm:h-24 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center border border-gray-100">
                  {firstImg ? (
                    <img
                      src={firstImg}
                      alt={p.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl">🏠</span>
                  )}
                </div>

                <div className="flex-1 min-w-0 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-bold text-gray-900 text-sm sm:text-base truncate">{p.title}</h3>
                      {getStatusBadge(p.status)}
                    </div>
                    <p className="text-gray-500 text-xs truncate">{p.city}{p.address ? `, ${p.address}` : ''} • {p.area} {t('sqm')}</p>
                    {p.status === 'rejected' && p.admin_notes && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs font-semibold text-red-800 mb-1">
                          {language === 'ru' ? 'Причина отказа:' : 'Rad etish sababi:'}
                        </p>
                        <p className="text-xs text-red-700">{p.admin_notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <p className="text-primary-600 font-extrabold text-sm sm:text-base">
                      {Number(p.price).toLocaleString()} <span className="text-xs font-semibold">{t('sum')}</span>
                    </p>
                    <div className="flex gap-2">
                      {p.status === 'pending' && (
                        <button
                          onClick={() => handleEditClick(p)}
                          className="text-blue-500 hover:text-blue-700 text-xs font-semibold px-2 py-1 hover:bg-blue-50 rounded transition"
                          title={language === 'ru' ? 'Редактировать' : 'Tahrirlash'}
                        >
                          ✏️ {language === 'ru' ? 'Редактировать' : 'Tahrirlash'}
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(p.id)}
                        className="text-red-400 hover:text-red-600 text-xs font-semibold px-2 py-1 hover:bg-red-50 rounded transition"
                        title={t('delete')}
                      >
                        🗑 {t('delete')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Topup Modal */}
      {showTopupModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 pb-20 shadow-2xl">
            <div className="flex justify-between items-center mb-4 pb-2 border-b">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                {language === 'ru' ? 'Пополнить баланс' : "Balansni to'ldirish"}
              </h2>
              <button onClick={() => { setShowTopupModal(false); setTopupInfo(null); }} className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 font-bold">×</button>
            </div>

            {!topupInfo ? (
              <>
                <p className="text-gray-500 text-xs sm:text-sm mb-3">
                  {language === 'ru' ? 'Выберите или введите сумму пополнения:' : "To'ldirish miqdorini tanlang yoki kiriting:"}
                </p>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[25000, 50000, 100000, 200000, 500000, 1000000].map(amt => (
                    <button
                      key={amt}
                      onClick={() => setTopupAmount(String(amt))}
                      className={`p-2 rounded-xl text-xs font-bold border-2 transition ${
                        topupAmount === String(amt)
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {amt.toLocaleString()}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  className="input mb-4 text-sm"
                  value={topupAmount}
                  onChange={e => setTopupAmount(e.target.value)}
                  placeholder="Summa..."
                />
                <button onClick={handleTopupRequest} className="btn btn-primary w-full py-3 font-bold text-sm">
                  {language === 'ru' ? 'Реквизиты для оплаты →' : "To'lov rekvizitlari →"}
                </button>
              </>
            ) : (
              <div className="space-y-3">
                <div className="bg-primary-50 rounded-xl p-4 border border-primary-100">
                  <p className="text-primary-900 font-bold text-sm mb-2">💳 {language === 'ru' ? 'Реквизиты для оплаты' : "To'lov rekvizitlari"}</p>
                  <p className="text-xs text-primary-800 mb-1">Karta: <strong className="font-mono">{topupInfo.payment_details?.card}</strong></p>
                  <p className="text-xs text-primary-800 mb-1">Summa: <strong>{Number(topupAmount).toLocaleString()} so'm</strong></p>
                  <p className="text-xs text-primary-800">Izoh: <strong>{topupInfo.payment_details?.comment}</strong></p>
                </div>
                <div className="bg-yellow-50 rounded-xl p-3 border border-yellow-200">
                  <p className="text-yellow-800 text-xs">⚡ {topupInfo.note}</p>
                </div>
                <button onClick={() => { setShowTopupModal(false); setTopupInfo(null); }} className="btn btn-secondary w-full py-3 font-bold text-sm">
                  {t('close')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Listing Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-lg p-5 sm:p-6 my-auto max-h-[92vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center mb-4 pb-2 border-b">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">{t('createListing')}</h2>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 font-bold">×</button>
            </div>

            {/* Balance check warning */}
            {listingPrice > 0 && !user?.is_admin && balance < listingPrice && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-xs sm:text-sm text-red-700">
                ⚠️ {language === 'ru' ? 'Недостаточно средств!' : 'Balans yetarli emas!'} Kerak: {listingPrice.toLocaleString()} {t('sum')}. Balansingiz: {balance.toLocaleString()} {t('sum')}.
                <button
                  onClick={() => { setShowCreateModal(false); setShowTopupModal(true); }}
                  className="ml-2 underline font-bold"
                >
                  {t('topup')} →
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3.5 text-sm">
              {/* PHOTOS */}
              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1.5">
                  📸 {t('photosLabel')} <span className="text-red-500">*</span>
                  <span className="text-gray-400 font-normal ml-1 text-xs">{t('photosHint')}</span>
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {photoPreviews.map((src, i) => (
                    <div key={i} className="relative aspect-square">
                      <img src={src} alt="" className="w-full h-full object-cover rounded-xl border" />
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center shadow font-bold"
                      >
                        ×
                      </button>
                      {i === 0 && (
                        <span className="absolute bottom-1 left-1 bg-primary-600 text-white text-[10px] rounded px-1 font-semibold">1</span>
                      )}
                    </div>
                  ))}
                  {photos.length < 7 && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-square border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-400 hover:border-primary-500 hover:text-primary-600 transition active:scale-95"
                    >
                      <span className="text-2xl">+</span>
                      <span className="text-[10px] font-semibold">{t('photosLabel')}</span>
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/heic,image/heif"
                  multiple
                  className="hidden"
                  onChange={handlePhotoSelect}
                />
                {photos.length < 2 && (
                  <p className="text-red-500 text-xs mt-1">⚠️ {t('uploadPhotosMinError')}</p>
                )}
              </div>

              {/* Property Type */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">{t('propertyType')}</label>
                <select
                  className="input text-sm"
                  value={formData.property_type}
                  onChange={e => setFormData({ ...formData, property_type: e.target.value as any })}
                >
                  <option value="apartment">{t('apartment')}</option>
                  <option value="house">{t('house')}</option>
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">{t('title')} *</label>
                <input
                  type="text"
                  className="input text-sm"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('titlePlaceholder')}
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">{t('description')} *</label>
                <textarea
                  className="input text-sm"
                  rows={3}
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('descPlaceholder')}
                  required
                />
              </div>

              {/* Price & Area */}
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('price')} ({t('sum')}) *</label>
                  <input type="number" className="input text-sm" value={formData.price}
                    onChange={e => setFormData({ ...formData, price: e.target.value })} required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('area')} ({t('sqm')}) *</label>
                  <input type="number" className="input text-sm" value={formData.area}
                    onChange={e => setFormData({ ...formData, area: e.target.value })} required />
                </div>
              </div>

              {/* Rooms & Floor */}
              <div className="grid grid-cols-3 gap-2.5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('rooms')}</label>
                  <input type="number" className="input text-sm" value={formData.rooms}
                    onChange={e => setFormData({ ...formData, rooms: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('floor')}</label>
                  <input type="number" className="input text-sm" value={formData.floor}
                    onChange={e => setFormData({ ...formData, floor: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('totalFloors')}</label>
                  <input type="number" className="input text-sm" value={formData.total_floors}
                    onChange={e => setFormData({ ...formData, total_floors: e.target.value })} />
                </div>
              </div>

              {/* City & Address */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('city')} *</label>
                  <input type="text" className="input text-sm" value={formData.city}
                    onChange={e => setFormData({ ...formData, city: e.target.value })}
                    placeholder={t('cityPlaceholder')} required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('address')} *</label>
                  <input type="text" className="input text-sm" value={formData.address}
                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                    placeholder={t('addressPlaceholder')} required />
                </div>
              </div>

              {/* Listing Fee info */}
              <div className="bg-primary-50 rounded-xl p-3 text-xs text-primary-900 border border-primary-100 flex items-center justify-between">
                <span>💰 {t('costPerListing')}:</span>
                <strong>{listingPrice === 0 ? t('freePosting') : `${listingPrice.toLocaleString()} ${t('sum')}`}</strong>
              </div>

              {/* Buttons */}
              <div className="flex gap-2.5 pt-2">
                <button
                  type="submit"
                  disabled={submitting || photos.length < 2}
                  className="btn btn-primary flex-1 font-bold py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow"
                >
                  {submitting ? t('loading') : t('create')}
                </button>
                <button type="button" onClick={() => { setShowCreateModal(false); resetForm(); }} className="btn btn-secondary font-bold py-3 text-sm">
                  {t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Listing Modal */}
      {showEditModal && editingProperty && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-lg p-5 sm:p-6 my-auto max-h-[92vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center mb-4 pb-2 border-b">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                {language === 'ru' ? 'Редактировать объявление' : "E'lonni tahrirlash"}
              </h2>
              <button onClick={() => { setShowEditModal(false); setEditingProperty(null); resetForm(); }} className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 font-bold">×</button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-xs sm:text-sm text-blue-700">
              ℹ️ {language === 'ru' ? 'Редактирование объявления на проверке бесплатно' : "Tekshirilayotgan e'lonni tahrirlash bepul"}
            </div>

            <form onSubmit={handleUpdateProperty} className="space-y-3.5 text-sm">
              {/* PHOTOS */}
              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1.5">
                  📸 {t('photosLabel')}
                  <span className="text-gray-400 font-normal ml-1 text-xs">{language === 'ru' ? '(можно добавить новые)' : "(yangi qo'shish mumkin)"}</span>
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {photoPreviews.map((src, i) => (
                    <div key={i} className="relative aspect-square">
                      <img src={src} alt="" className="w-full h-full object-cover rounded-xl border" />
                      {photos.length > 0 && i >= editingProperty.images.length && (
                        <button
                          type="button"
                          onClick={() => removePhoto(i)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center shadow font-bold"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  {photoPreviews.length < 7 && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-square border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-400 hover:border-primary-500 hover:text-primary-600 transition active:scale-95"
                    >
                      <span className="text-2xl">+</span>
                      <span className="text-[10px] font-semibold">{t('photosLabel')}</span>
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/heic,image/heif"
                  multiple
                  className="hidden"
                  onChange={handlePhotoSelect}
                />
              </div>

              {/* Property Type */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">{t('propertyType')}</label>
                <select
                  className="input text-sm"
                  value={formData.property_type}
                  onChange={e => setFormData({ ...formData, property_type: e.target.value as any })}
                >
                  <option value="apartment">{t('apartment')}</option>
                  <option value="house">{t('house')}</option>
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">{t('title')} *</label>
                <input
                  type="text"
                  className="input text-sm"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('titlePlaceholder')}
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">{t('description')} *</label>
                <textarea
                  className="input text-sm"
                  rows={3}
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('descPlaceholder')}
                  required
                />
              </div>

              {/* Price & Area */}
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('price')} ({t('sum')}) *</label>
                  <input type="number" className="input text-sm" value={formData.price}
                    onChange={e => setFormData({ ...formData, price: e.target.value })} required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('area')} ({t('sqm')}) *</label>
                  <input type="number" className="input text-sm" value={formData.area}
                    onChange={e => setFormData({ ...formData, area: e.target.value })} required />
                </div>
              </div>

              {/* Rooms & Floor */}
              <div className="grid grid-cols-3 gap-2.5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('rooms')}</label>
                  <input type="number" className="input text-sm" value={formData.rooms}
                    onChange={e => setFormData({ ...formData, rooms: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('floor')}</label>
                  <input type="number" className="input text-sm" value={formData.floor}
                    onChange={e => setFormData({ ...formData, floor: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('totalFloors')}</label>
                  <input type="number" className="input text-sm" value={formData.total_floors}
                    onChange={e => setFormData({ ...formData, total_floors: e.target.value })} />
                </div>
              </div>

              {/* City & Address */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('city')} *</label>
                  <input type="text" className="input text-sm" value={formData.city}
                    onChange={e => setFormData({ ...formData, city: e.target.value })}
                    placeholder={t('cityPlaceholder')} required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('address')} *</label>
                  <input type="text" className="input text-sm" value={formData.address}
                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                    placeholder={t('addressPlaceholder')} required />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-2.5 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary flex-1 font-bold py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow"
                >
                  {submitting ? t('loading') : (language === 'ru' ? 'Сохранить' : 'Saqlash')}
                </button>
                <button type="button" onClick={() => { setShowEditModal(false); setEditingProperty(null); resetForm(); }} className="btn btn-secondary font-bold py-3 text-sm">
                  {t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Phone Required Modal */}
      {showPhoneRequiredModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 animate-fadeIn">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl animate-slideUp">
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <span className="text-4xl">📱</span>
              </div>

              <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-3">
                {language === 'ru' ? 'Требуется номер телефона' : 'Telefon raqami kerak'}
              </h2>

              <p className="text-gray-600 text-sm sm:text-base mb-6 leading-relaxed">
                {language === 'ru'
                  ? 'Для размещения объявлений необходимо поделиться номером телефона в Telegram боте. Это нужно, чтобы покупатели могли с вами связаться.'
                  : "E'lon joylashtirish uchun Telegram botda telefon raqamingizni ulashishingiz kerak. Bu xaridorlar siz bilan bog'lanishi uchun zarur."}
              </p>

              <div className="bg-primary-50 border-2 border-primary-100 rounded-2xl p-4 mb-6">
                <p className="text-sm text-primary-900 font-semibold mb-2">
                  {language === 'ru' ? '📋 Как это сделать:' : '📋 Qanday qilish kerak:'}
                </p>
                <ol className="text-left text-sm text-primary-800 space-y-2">
                  <li>1. {language === 'ru' ? 'Откройте Telegram бот' : 'Telegram botni oching'}</li>
                  <li>2. {language === 'ru' ? 'Нажмите "Поделиться контактом"' : '"Kontaktni ulashish" ni bosing'}</li>
                  <li>3. {language === 'ru' ? 'Вернитесь сюда и создайте объявление' : "Bu yerga qaytib, e'lon yarating"}</li>
                </ol>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={openTelegramBot}
                  className="btn btn-primary flex-1 py-3.5 font-bold text-sm shadow-lg"
                >
                  <span className="mr-2">✈️</span>
                  {language === 'ru' ? 'Открыть бота' : 'Botni ochish'}
                </button>
                <button
                  onClick={() => setShowPhoneRequiredModal(false)}
                  className="btn btn-secondary py-3.5 font-bold text-sm"
                >
                  {language === 'ru' ? 'Позже' : 'Keyinroq'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
